/**
 * Zobrazení úkolů: přehled „Co je potřeba udělat“ na úvodní obrazovce,
 * pruh „Pozor“ na kartě včelstva a barvy dlaždic.
 */
import { useState } from 'react';
import type { Colony, Queen } from '../db/model';
import { getSetting, read } from '../db/db';
import { SEASON_SETTING, normStartMonth } from '../db/season';
import { DEFAULT_FEED_TARGET, FEED_TARGET_SETTING } from '../db/reports';
import { todayISO } from '../db/rules';
import { apiaryTasks, byUrgency, colonyTasks, colonyTone, eventsByColony, type Task, type TaskOptions } from '../db/tasks';
import { useQuery } from './hooks';
import { colonyName } from './labels';
import { DEFAULT_QUEEN_AGE, QUEEN_AGE_SETTING } from './queen-age';
import { Icon, type IconName } from './icons';
import { Link } from './ui';

export async function loadTaskOptions(): Promise<TaskOptions> {
  return {
    today: todayISO(),
    seasonStartMonth: normStartMonth((await getSetting<number>(SEASON_SETTING)) ?? 1),
    queenAgeLimit: (await getSetting<number>(QUEEN_AGE_SETTING)) ?? DEFAULT_QUEEN_AGE,
    feedTarget: (await getSetting<number>(FEED_TARGET_SETTING)) ?? DEFAULT_FEED_TARGET,
  };
}

export const useTaskOptions = () => useQuery(loadTaskOptions, []);

export interface AllTasks {
  tasks: Task[];
  tone: Map<string, 'ok' | 'warn' | 'danger'>;
  colonies: Map<string, Colony>;
}

/** Úkoly všech včelstev a stanovišť – jedno čtení dat, jedno projití zápisů. */
/** `onlyApiary`: jen včelstva jednoho stanoviště (barvy dlaždic) – rychlejší u velkých včelařů. */
export async function loadAllTasks(onlyApiary?: string): Promise<AllTasks> {
  const [apiaries, colonies, queens, o] = await Promise.all([read.all('apiaries'), read.all('colonies'), read.all('queens'), loadTaskOptions()]);
  // pro jedno stanoviště stačí zápisy jeho včelstev (přes index), jinak všechny
  const events = onlyApiary
    ? (
        await Promise.all(
          colonies.filter((c) => c.apiaryId === onlyApiary && c.status === 'active').map((c) => read.byIndex('events', 'colonyIds', c.id)),
        )
      ).flat()
    : await read.all('events');
  const live = apiaries.filter((a) => !a.archived);
  const liveIds = new Set(live.map((a) => a.id));
  const active = colonies.filter((c) => c.status === 'active' && liveIds.has(c.apiaryId) && (!onlyApiary || c.apiaryId === onlyApiary));
  const queenById = new Map<string, Queen>(queens.map((q) => [q.id, q]));
  const grouped = eventsByColony(events);
  const tasks: Task[] = [];
  const tone = new Map<string, 'ok' | 'warn' | 'danger'>();
  for (const c of active) {
    const t = colonyTasks(c, grouped.get(c.id) ?? [], c.currentQueenId ? queenById.get(c.currentQueenId) : undefined, o);
    tone.set(c.id, colonyTone(t));
    tasks.push(...t.filter((x) => !x.notice));
  }
  if (!onlyApiary) for (const a of live) tasks.push(...apiaryTasks(a, active, grouped, o.today));
  return { tasks: tasks.sort(byUrgency), tone, colonies: new Map(colonies.map((c) => [c.id, c])) };
}

export const useAllTasks = (onlyApiary?: string) => useQuery(() => loadAllTasks(onlyApiary), [onlyApiary]);

export function TaskRow({ t, who }: { t: Task; who?: string }) {
  return (
    <li>
      <Link to={t.to} className={'alert-row ' + (t.tone === 'info' ? 'info' : t.tone === 'danger' ? 'danger' : 'warn')}>
        <span className="alert-icon" aria-hidden>
          <Icon name={t.icon as IconName} size={24} />
        </span>
        <span className="alert-text">
          {who && <strong className="alert-who">{who}</strong>}
          {t.text}
        </span>
        <span className="tile-chev">›</span>
      </Link>
    </li>
  );
}

const OPEN_KEY = 'ulnik-tasks-open';
const isOpen = () => {
  try {
    return sessionStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
};

/** Úvodní obrazovka (dole): rozbalovací lišta „Co je potřeba udělat“. */
export function TasksPanel() {
  const all = useAllTasks();
  const [open, setOpen] = useState(isOpen);
  if (!all || all.colonies.size === 0) return null;
  const { tasks, colonies } = all;
  if (!tasks.length)
    return (
      <p className="tasks-ok">
        <Icon name="check" size={20} /> Žádné úkoly – u včelstev je vše v pořádku.
      </p>
    );
  const urgent = tasks.some((t) => t.tone === 'danger');
  return (
    <details
      className={'alerts tasks' + (urgent ? ' urgent' : '')}
      open={open}
      onToggle={(e) => {
        const o = (e.currentTarget as HTMLDetailsElement).open;
        setOpen(o);
        try {
          sessionStorage.setItem(OPEN_KEY, o ? '1' : '0');
        } catch {
          /* nevadí */
        }
      }}
    >
      <summary className="tasks-summary">
        <Icon name="clipboard" size={22} />
        <span className="grow">Co je potřeba udělat</span>
        <span className={'tasks-count' + (urgent ? ' urgent' : '')}>{tasks.length}</span>
      </summary>
      <ul>
        {tasks.map((t, i) => (
          <TaskRow key={i} t={t} who={t.colonyId ? colonyName(colonies.get(t.colonyId)?.label) : undefined} />
        ))}
      </ul>
    </details>
  );
}

/** Karta včelstva: pruh „Pozor“ se všemi úkoly a upozorněními tohoto včelstva. */
export function ColonyTasks({ colony, events, queen }: { colony: Colony; events: Parameters<typeof colonyTasks>[1]; queen?: Queen }) {
  const o = useTaskOptions();
  if (!o) return null;
  const items = colonyTasks(colony, events, queen, o).sort(byUrgency);
  if (!items.length) return null;
  return (
    <section className="alerts" aria-label="Pozor">
      <div className="alerts-head">Pozor{items.length > 1 ? ` (${items.length})` : ''}</div>
      <ul>
        {items.map((t, i) => (
          <TaskRow key={i} t={t} />
        ))}
      </ul>
    </section>
  );
}
